/**
 * Analyzer Page Utilities
 *
 * Helper functions for detecting and working with the 3-step analyzer flow
 * and managing analyzer session state
 */

// ==================== ROUTING UTILITIES ====================

/**
 * Check if current pathname is any of the analyzer pages (steps 1, 2, 3, or 3a high-risk)
 * @param pathname - The current location.pathname
 * @returns true if on any analyzer page
 */
export function isAnalyzerPage(pathname: string): boolean {
  return (
    pathname === '/analyze' ||
    pathname === '/analyze/results' ||
    pathname === '/analyze/response' ||
    pathname === '/analyze/high-risk' ||
    pathname === '/analyze/thread'
  )
}

/**
 * Get the current analyzer step based on pathname
 * @param pathname - The current location.pathname
 * @returns Step number (1, 2, 3, or '3a' for high-risk) or undefined if not on analyzer
 */
export function getAnalyzerStep(pathname: string): number | string | undefined {
  if (pathname === '/analyze') return 1
  if (pathname === '/analyze/results') return 2
  if (pathname === '/analyze/response') return 3
  if (pathname === '/analyze/high-risk') return '3a'
  return undefined
}

// ==================== SAMPLE MESSAGE PREFILL ====================

/** Prefill for "Try Sample Message" in welcome tours (shared across HouseholdDashboardTour and MessageAnalyzerTour) */
export const SAMPLE_MESSAGE_PREFILL = {
  relationship: 'friend',
  message: "It's totally fine, I figured you'd be busy.",
  context:
    "Something came up and you had to cancel plans. I cant tell if they're upset or just understanding.",
} as const

// ==================== SESSION MANAGEMENT ====================

/**
 * All analyzer-related sessionStorage keys
 * These are the ONLY keys that should be cleared during analyzer restart
 */
const ANALYZER_SESSION_KEYS = [
  'analyzer_session_state', // Main step state (input, results, response)
  'analyzer_full_state', // Full chat state for Step 3
  'lina-response-draft-scenario', // Demo scenario for response drafts
  'analyzer_risk_modal_shown_this_session', // Track if risk modal opened this session
  'response_event_ids', // Array of response event IDs from Step 3
  'current_response_event_id', // Current response event ID from Step 3
  'current_analysis_event_id', // Current analysis event ID from Step 2
] as const

/**
 * Clear all analyzer-related session data
 *
 * This removes ONLY analyzer session keys, preserving:
 * - Authentication tokens
 * - User preferences
 * - Other app state
 *
 * Use this for "Start Over" / "Restart Analysis" functionality
 */
export function clearAnalyzerSession(): void {
  ANALYZER_SESSION_KEYS.forEach((key) => {
    sessionStorage.removeItem(key)
  })
}

/**
 * Restart the analyzer with a clean slate
 *
 * This is the canonical way to restart the entire analyzer flow:
 * 1. Clears all analyzer session data
 * 2. Hard redirects to homepage (ensures clean state)
 *
 * Use this from any restart/start-over button or handler
 */
export function restartAnalyzer(): void {
  console.log('[AnalyzerSession] Restarting analyzer...')
  clearAnalyzerSession()

  // Use hard redirect to ensure clean state (no React router caching)
  window.location.href = '/'
}

/**
 * Navigate to homepage (which will provide clean entry to analyzer)
 *
 * Use this when you want to programmatically navigate to homepage
 * and trigger fresh state initialization (soft restart via React Router)
 *
 * @param navigate - React Router navigate function
 */
export function navigateToAnalyzerFresh(navigate: (path: string, options?: any) => void): void {
  console.log('[AnalyzerSession] Navigating to homepage with fresh start...')
  clearAnalyzerSession()
  navigate('/', {
    replace: true,
  })
}

/**
 * Get analyzer session state (if exists and valid)
 * @returns Parsed session state or null if invalid/expired
 */
export function getAnalyzerSessionState(): any | null {
  try {
    const sessionState = sessionStorage.getItem('analyzer_session_state')
    if (!sessionState) return null

    const parsed = JSON.parse(sessionState)
    const ageInMinutes = (Date.now() - parsed.timestamp) / 1000 / 60

    // Expire after 120 minutes
    if (ageInMinutes >= 120) {
      console.log('[AnalyzerSession] Session expired, clearing...')
      clearAnalyzerSession()
      return null
    }

    return parsed
  } catch (err) {
    console.error('[AnalyzerSession] Failed to parse session state:', err)
    clearAnalyzerSession()
    return null
  }
}

/**
 * Save analyzer session state
 * @param state - Session state object to save
 */
export function saveAnalyzerSessionState(state: any): void {
  try {
    const stateWithTimestamp = {
      ...state,
      timestamp: Date.now(),
    }
    sessionStorage.setItem('analyzer_session_state', JSON.stringify(stateWithTimestamp))
  } catch (err) {
    console.error('[AnalyzerSession] Failed to save session state:', err)
  }
}
